import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  Button,
  DataTableSkeleton,
  Link,
  RadioButton,
  Tag,
} from '@carbon/react';
import { ColumnFilter } from 'components/table/column-filter';
import { TableActions } from 'components/table/table-actions';
import { DebouncedInput } from 'components/debounced-input/debounced-input';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import { useColumnFilter } from 'lib/hooks/useColumnFilter';
import { DECIMAL_FORMATTER, DECIMAL_FORMATTER_TWO_FRACTIONS } from 'lib/ui';
import { format2 } from '../../utils/number';

import { emptyPinStyleGenerator } from 'components/allocation-manager/table';
import { capitalize } from 'lodash-es';
import classnames from 'classnames';
import { Fragment, useMemo, useState } from 'react';
import { WellIntersectionAnalysisResponse } from 'types/api-responses';
import { formatUnitAgreementType } from 'components/allocation-manager/utils';
import style from './well-unit-details-table.module.scss';

export const NULL_DISPLAY_VALUE = '--';

interface UnitDetailsTableRow {
  id: string;
  unitId: string;
  unitAgreementType: string;
  assignedToUnit: string;
  assignmentType: string;
  allocationMethod: string;
  coveragePercentage: number;
  platBased: string;
  acreage: number;
  length: number;
  width: number;
  isAssigned: boolean;
  /** The backend's recommendation. Absent outside a Well Workspace — see IntersectingUnit. */
  isBestFit: boolean;
}

/**
 * The assignment control, for a well authored in a Well Workspace (Epic 3 Story 1).
 *
 * Omitted everywhere else — the Well page reads a canonical well, whose unit assignment is
 * derived rather than chosen, so it gets the table exactly as it is today.
 */
export interface UnitAssignmentControls {
  /** The unit the radio has picked, by name. Null before the rows have loaded. */
  pickedUnitId: string | null;
  onPick: (unitId: string) => void;
  /** Stages the picked unit. Only called when the pick differs from the standing one. */
  onAssign: () => void;
  /** True while the pick is in flight, to hold the button. */
  assigning: boolean;
}

interface UnitDetailsProps {
  data: WellIntersectionAnalysisResponse;
  selectedUnitIds: Set<string>;
  onSelectionChange: (unitId: string, selected: boolean) => void;
  /** Present only for an authored well; absent leaves the table unchanged. */
  assignment?: UnitAssignmentControls;
}

const formatAssignmentType = (
  assignmentType: 'explicit' | 'best_fit' | 'none'
): string => {
  switch (assignmentType) {
    case 'explicit':
      return 'Explicit';
    case 'best_fit':
      return 'Best Fit';
    case 'none':
      return '--';
    default:
      return '--';
  }
};

const formatAllocationMethod = (
  allocationMethod: string | null | undefined
): string => {
  if (!allocationMethod) return '--';
  return allocationMethod.split('_').map(capitalize).join(' ');
};

// DRY: Reusable filter function for string columns
export const createStringFilterFn = () => {
  return (
    row: { getValue: (columnId: string) => unknown },
    columnId: string,
    filterValue: string
  ) => {
    const value = row.getValue(columnId);
    if (!filterValue) return true;
    if (!value || value === NULL_DISPLAY_VALUE) return false;
    return String(value).toLowerCase().includes(filterValue.toLowerCase());
  };
};

// DRY: Reusable filter function for formatted numeric columns
export const createFormattedNumericFilterFn = (
  formatter: (value: number) => string
) => {
  return (
    row: { getValue: (columnId: string) => unknown },
    columnId: string,
    filterValue: string
  ) => {
    const value = row.getValue(columnId) as number;
    if (!filterValue) return true;
    if (value === null || value === undefined)
      return filterValue === NULL_DISPLAY_VALUE;
    const formattedValue = formatter(value);
    return formattedValue.toLowerCase().includes(filterValue.toLowerCase());
  };
};

const columnHelper = createColumnHelper<UnitDetailsTableRow>();

const UnitDetails = ({
  data,
  selectedUnitIds,
  onSelectionChange,
  assignment,
}: UnitDetailsProps) => {
  const tableId = 'well-unit-details-table';
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnSizing, setColumnSizing] = useColumnSizing(tableId);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');

  const {
    columnFilters,
    setColumnFilters,
    isColumnFilterActive,
    setIsColumnFilterActive,
  } = useColumnFilter(tableId);

  const tableData = useMemo(
    () =>
      data.intersectingUnits.map((unit) => ({
        id: unit.name,
        unitId: unit.name,
        unitAgreementType: formatUnitAgreementType(unit.unitAgreementType),
        assignedToUnit: unit.assignedToUnit ? 'Yes' : 'No',
        assignmentType: formatAssignmentType(unit.assignmentType),
        allocationMethod: formatAllocationMethod(unit.allocationMethod),
        coveragePercentage: unit.coveragePercentage,
        platBased: unit.platBased ? 'Yes' : 'No',
        acreage: unit.acreage,
        length: unit.length,
        width: unit.width,
        isAssigned: unit.assignedToUnit,
        isBestFit: unit.bestFit ?? false,
      })),
    [data.intersectingUnits]
  );

  // Check if there's an assigned unit
  const hasAssignedUnit = useMemo(
    () => tableData.some((row) => row.isAssigned),
    [tableData]
  );

  // Handle row click to toggle selection
  const handleRowClick = (rowId: string, isAssigned: boolean) => {
    // If this is the assigned unit, don't allow deselection
    if (isAssigned && hasAssignedUnit) {
      return;
    }

    const isCurrentlySelected = selectedUnitIds.has(rowId);
    onSelectionChange(rowId, !isCurrentlySelected);
  };

  // Helper to wrap content in bold for assigned unit rows
  const wrapBold = (content: string | JSX.Element, isAssigned: boolean) => {
    if (isAssigned) {
      return <strong>{content}</strong>;
    }
    return content;
  };

  const columns = useMemo(
    () => [
      // The pick column, first and unsortable — it belongs to the row rather than describing
      // it. Only built for an authored well; the Well page's table is unchanged without it.
      ...(assignment
        ? [
            columnHelper.display({
              id: 'unitPick',
              header: '',
              size: 48,
              enableResizing: false,
              enableColumnFilter: false,
              cell: ({ row }) => (
                // The row itself is clickable — it scopes the map to that unit — so the pick
                // is held back from it. Picking a unit to assign and scoping the map to one
                // are different decisions, and one click must not make both. The Unit ID
                // link guards itself the same way.
                <span
                  onClick={(event: React.MouseEvent) => event.stopPropagation()}
                  role="presentation"
                >
                  <RadioButton
                    id={`unit-pick-${row.original.id}`}
                    name="unit-assignment"
                    labelText=""
                    aria-label={`Assign ${row.original.unitId} to this well`}
                    checked={assignment.pickedUnitId === row.original.id}
                    disabled={assignment.assigning}
                    onChange={() => assignment.onPick(row.original.id)}
                  />
                </span>
              ),
            }),
          ]
        : []),
      columnHelper.accessor('unitId', {
        id: 'unitId',
        header: 'Unit ID',
        size: assignment ? 260 : 150,
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (!value) return NULL_DISPLAY_VALUE;

          const content = (
            <Link
              href={`/development_areas/${value}`}
              target="_blank"
              rel="noopener noreferrer"
              className={style.unitIdLink}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {value}
            </Link>
          );

          // Two different facts, so two different tags: Assigned is the unit the well
          // HOLDS, Best fit is the one the backend RECOMMENDS. A unit can be either,
          // both, or neither, and collapsing them would hide a disagreement worth seeing.
          return (
            <span className={style.unitIdCell}>
              {wrapBold(content, row.original.isAssigned)}
              {row.original.isAssigned && (
                <Tag type="green" size="sm">
                  Assigned
                </Tag>
              )}
              {row.original.isBestFit && (
                <Tag type="blue" size="sm">
                  Best fit
                </Tag>
              )}
            </span>
          );
        },
        filterFn: createStringFilterFn(),
      }),
      // Beside Unit ID because both identify the agreement rather than describe it.
      // Already formatted in tableData via the shared formatUnitAgreementType, so
      // the label here cannot drift from the unit cards or the certification page.
      columnHelper.accessor('unitAgreementType', {
        id: 'unitAgreementType',
        header: 'Unit Agreement Type',
        size: 170,
        cell: ({ getValue, row }) => {
          const content = getValue() || NULL_DISPLAY_VALUE;
          return wrapBold(content, row.original.isAssigned);
        },
        filterFn: createStringFilterFn(),
      }),
      columnHelper.accessor('assignedToUnit', {
        id: 'assignedToUnit',
        header: 'Assigned to Unit',
        size: 140,
        cell: ({ getValue, row }) => {
          const content = getValue() || NULL_DISPLAY_VALUE;
          return wrapBold(content, row.original.isAssigned);
        },
        filterFn: createStringFilterFn(),
      }),
      columnHelper.accessor('assignmentType', {
        id: 'assignmentType',
        header: 'Assignment Type',
        size: 140,
        cell: ({ getValue, row }) => {
          const content = getValue() || NULL_DISPLAY_VALUE;
          return wrapBold(content, row.original.isAssigned);
        },
        filterFn: createStringFilterFn(),
      }),
      columnHelper.accessor('allocationMethod', {
        id: 'allocationMethod',
        header: 'Allocation Method',
        size: 150,
        cell: ({ getValue, row }) => {
          const content = getValue() || NULL_DISPLAY_VALUE;
          return wrapBold(content, row.original.isAssigned);
        },
        filterFn: createStringFilterFn(),
      }),
      columnHelper.accessor('coveragePercentage', {
        id: 'coveragePercentage',
        header: 'Coverage',
        size: 100,
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (value === null || value === undefined) return NULL_DISPLAY_VALUE;
          const percentage = value * 100;
          const content = `${format2(percentage)}%`;
          return wrapBold(content, row.original.isAssigned);
        },
        filterFn: createFormattedNumericFilterFn((v) => {
          const percentage = v * 100;
          return `${format2(percentage)}%`;
        }),
      }),
      columnHelper.accessor('platBased', {
        id: 'platBased',
        header: 'Plat Based',
        size: 100,
        cell: ({ getValue, row }) => {
          const content = getValue() || NULL_DISPLAY_VALUE;
          return wrapBold(content, row.original.isAssigned);
        },
        filterFn: createStringFilterFn(),
      }),
      columnHelper.accessor('acreage', {
        id: 'acreage',
        header: 'Gross Acreage (GIS)',
        size: 150,
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (!value && value !== 0) return NULL_DISPLAY_VALUE;
          const content = DECIMAL_FORMATTER_TWO_FRACTIONS.format(value);
          return wrapBold(content, row.original.isAssigned);
        },
        filterFn: createFormattedNumericFilterFn((v) =>
          DECIMAL_FORMATTER_TWO_FRACTIONS.format(v)
        ),
      }),
      columnHelper.accessor('length', {
        id: 'length',
        header: 'Unit Tract Length (GIS)',
        size: 170,
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (!value && value !== 0) return NULL_DISPLAY_VALUE;
          const content = DECIMAL_FORMATTER.format(Math.trunc(value)) + ' ft';
          return wrapBold(content, row.original.isAssigned);
        },
        filterFn: createFormattedNumericFilterFn(
          (v) => DECIMAL_FORMATTER.format(Math.trunc(v)) + ' ft'
        ),
      }),
      columnHelper.accessor('width', {
        id: 'width',
        header: 'Unit Tract Width (GIS)',
        size: 160,
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (!value && value !== 0) return NULL_DISPLAY_VALUE;
          const content = DECIMAL_FORMATTER.format(Math.trunc(value)) + ' ft';
          return wrapBold(content, row.original.isAssigned);
        },
        filterFn: createFormattedNumericFilterFn(
          (v) => DECIMAL_FORMATTER.format(Math.trunc(v)) + ' ft'
        ),
      }),
    ],
    // The pick column closes over the standing pick and the in-flight flag, so the columns
    // are rebuilt with them. Without this the radio would paint the pick it opened with.
    [assignment]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      columnSizing,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: {
      size: 150,
      minSize: 50,
      enableResizing: true,
      enableColumnFilter: true,
    },
    initialState: {
      columnSizing: {},
    },
  });

  const { rows } = table.getRowModel();
  const isColumnVisible = table.getVisibleLeafColumns().length > 0;

  const assignedUnit = data.intersectingUnits.find((u) => u.assignedToUnit);
  const assignmentTypeText =
    assignedUnit?.assignmentType === 'explicit' ? 'explicitly' : 'best fit';
  const intersectingOnlyCount =
    data.intersectingUnitsCount - data.assignedUnitCount;

  if (!data) {
    return (
      <DataTableSkeleton
        showHeader={false}
        columnCount={columns.length}
        rowCount={1}
        showToolbar={false}
      />
    );
  }

  return (
    <>
      <div className={style.headerText}>
        {data.assignedUnitCount > 0 ? (
          <>
            This well is {assignmentTypeText} assigned to{' '}
            {data.assignedUnitCount === 1
              ? 'a Unit'
              : `${data.assignedUnitCount} Units`}
            .{' '}
            {/* Discount every assigned unit, not just one: a well can be assigned
                under several agreements, and counting only one leaves the rest
                described as merely intersecting. */}
            {intersectingOnlyCount > 0
              ? `${intersectingOnlyCount} other ${
                  intersectingOnlyCount === 1
                    ? 'unit intersects'
                    : 'units intersect'
                } this well.`
              : ''}
          </>
        ) : (
          <>
            This well has no assigned best fit or explicit unit.{' '}
            {data.intersectingUnitsCount} other units intersect this well.
          </>
        )}
      </div>

      <div className={style.unitDetailsArea}>
        <div className={style.tableContext}>
          <div className={style.searchContainer}>
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={(value) => setGlobalFilter(String(value).trim())}
              placeholder="Search table"
              className={style.unitSearchInput}
              data-testid="unit-search-input"
              debounce={300}
            />
          </div>
          {/* Enabled only when the pick differs from the standing assignment — assigning a
              well to the unit it already holds writes nothing, so the button says so by
              staying inert rather than by answering with a no-op. */}
          {assignment && (
            <Button
              size="md"
              kind="primary"
              className={style.assignButton}
              disabled={
                assignment.assigning ||
                assignment.pickedUnitId === null ||
                assignment.pickedUnitId === assignedUnit?.name
              }
              onClick={assignment.onAssign}
            >
              {assignment.assigning ? 'Assigning…' : 'Assign to well'}
            </Button>
          )}
          <TableActions
            table={table}
            isColumnFilterActive={isColumnFilterActive}
            setIsColumnFilterActive={setIsColumnFilterActive}
          />
        </div>

        {isColumnVisible ? (
          <div className={style.unitDetailsTableContainer}>
            <div className={style.unitDetailsTableArea}>
              <table
                className={style.unitDetailsTable}
                style={{ width: table.getTotalSize() }}
              >
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Fragment key={headerGroup.id}>
                      <tr>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={style.unitDetailsTableHeader}
                            style={{
                              width: header.getSize(),
                              position: 'relative',
                            }}
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                className={
                                  header.column.getCanSort()
                                    ? 'cursor-pointer select-none'
                                    : ''
                                }
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                {header.column.getIsSorted() as string}
                              </div>
                            )}
                            {header.column.getCanResize() && (
                              <div
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                className={`${style.resizer} ${
                                  header.column.getIsResizing()
                                    ? style.isResizing
                                    : ''
                                }`}
                              />
                            )}
                          </th>
                        ))}
                      </tr>
                      {isColumnFilterActive ? (
                        <tr>
                          <ColumnFilter
                            key={`filter-row-${headerGroup.id}`}
                            pinStyleGenerator={emptyPinStyleGenerator}
                            headers={headerGroup.headers}
                          />
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isSelected = selectedUnitIds.has(row.original.id);
                    const isAssigned = row.original.isAssigned;

                    return (
                      <tr
                        key={row.id}
                        onClick={() =>
                          handleRowClick(row.original.id, isAssigned)
                        }
                        className={classnames({
                          [style.selectedRow]: isSelected,
                          [style.assignedRow]: isAssigned,
                          [style.clickableRow]: !isAssigned || !hasAssignedUnit,
                        })}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={style.unitDetailsTableCell}
                            style={{ width: cell.column.getSize() }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className={style.hiddenColumn}>
            No columns are visible. Use the column visibility controls to show
            columns.
          </div>
        )}

        {/* Row count display */}
        <div className={style.totalRowCount}>
          <span className={style.totalRows}>{rows.length}</span>
          {rows.length === 1 ? 'row' : 'rows'}
        </div>
      </div>
    </>
  );
};

export default UnitDetails;
